import {Directive, ElementRef, Input, OnInit} from '@angular/core';
import {take} from 'rxjs/operators';

import PermissionLevel from '../../permission/enums/permissionLevel.enum';
import {FinancialPermissionLevel} from '../../permission/enums/financialPermissionLevel.enum';
import SectionsAccess from '../../permission/enums/sectionsAccess.enum';
import {PermissionService} from '../../permission/permission.service';
import {DialogService} from '../../services/dialog.service';
import {AlertDialogParams} from '../components/alert-dialog/alert-dialog.component';
import ViewAsInvestorPermission from '../../permission/enums/view-as-investor-permission';

export enum NoPermissionAction {
  Hide = 0,
  Blur = 1,
  PopupOnClick = 2
}

@Directive({
  selector: '[terraCheckPermissions]',
  standalone: true,
})
export class CheckPermissionsDirective  implements OnInit {
  @Input() minimumPermissionLevel: PermissionLevel;
  @Input() minimumFinancialPermissionLevel: FinancialPermissionLevel;
  @Input() sectionsAccess: SectionsAccess;
  @Input() noPermissionAction: NoPermissionAction;
  @Input() requireContactsVisibility = false;
  @Input() minimumViewAsInvestorPermission: ViewAsInvestorPermission;

  constructor(private elementRef: ElementRef,
              private permissionService: PermissionService,
              private dialogService: DialogService) {
  }

  ngOnInit() {
    this.permissionService.checkPermission(this.minimumPermissionLevel, this.minimumFinancialPermissionLevel, this.sectionsAccess,
                                           this.requireContactsVisibility, this.minimumViewAsInvestorPermission).pipe(
      take(1)
    ).subscribe(([allowed, failedForType]) => {
        if (!allowed) {
          switch (this.noPermissionAction) {
            case NoPermissionAction.Hide:
              this.elementRef.nativeElement.style.display = 'none';
              break;
            case NoPermissionAction.Blur:
              this.elementRef.nativeElement.style.filter = 'blur(4px)';
              this.elementRef.nativeElement.style['pointer-events'] = 'none';
              this.elementRef.nativeElement.style['user-select'] = 'none';
              break;
            case NoPermissionAction.PopupOnClick:
              this.elementRef.nativeElement.removeAllListeners('click');
              this.elementRef.nativeElement.addEventListener('click', this.openPopup.bind(this, failedForType));
              break;
            default:
              this.elementRef.nativeElement.style.display = 'none';
          }
        }
      }
    );
  }

  private openPopup(failedForType: string) {
    event.stopPropagation();
    event.preventDefault();
    let title;
    let message;
    switch (failedForType) {
      case 'PermissionLevel':
        if (this.minimumPermissionLevel === PermissionLevel.Editor) {
          title = 'You do not have permissions to perform this action';
          message = 'You don\'t have permission to update this resource.';
        } else if (this.minimumPermissionLevel === PermissionLevel.Admin) {
          title = 'You do not have permissions to perform this action';
          message = 'You don\'t have permission to access this resource.';
        }
        break;
      case 'FinancialPermissionLevel':
        if (this.minimumFinancialPermissionLevel === FinancialPermissionLevel.NonSignatory) {
          title = 'You do not have permissions to perform this action';
          message = 'You don\'t have permission to access this resource.';
        } else if (this.minimumFinancialPermissionLevel === FinancialPermissionLevel.AuthorizedSignatory) {
          title = 'You do not have financial-signatory permissions';
          message = 'You do not have permissions to finalize financial transactions. \r\nThis action can only be finalized by a user with financial-signatory permissions.';
        }
        break;
      case 'SectionsAccess':
        title = 'You do not have permissions to perform this action';
        message = 'You don\'t have permission to access this resource.';
        break;
      case 'requireContactsVisibility':
        title = 'You do not have permissions to perform this action';
        message = 'You don\'t have permission to access this resource.';
        break;
      default:
        title = 'You do not have permissions to perform this action';
        message = 'You don\'t have permission to access this resource.';
        break;
    }

    this.dialogService.alertDialog(new AlertDialogParams(title, message));
  }
}
